import { useState, useEffect, useMemo } from 'react';
import { CalendarEventData, DayEventsResponse, BlockDaysRequest, RescheduleEventRequest } from '../types/billing-calendar';
import { CalendarDay } from '../types/schedule';
import api from '../lib/api';
import { toast } from '../hooks/use-toast';

interface UseBillingCalendarProps {
  clientId: number;
}

export const useBillingCalendar = ({ clientId }: UseBillingCalendarProps) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarData, setCalendarData] = useState<CalendarEventData | null>(null);
  const [dayEvents, setDayEvents] = useState<DayEventsResponse | null>(null);
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth() + 1;

  // Buscar dados do calendário
  const fetchCalendarData = async () => {
    if (!clientId) return;

    setLoading(true);
    setError(null);

    try {
      console.log('📅 [useBillingCalendar] Buscando calendário para cliente:', clientId, 'ano:', year, 'mês:', month);
      const response = await api.get<any>(
        `/billing-rules/calendar/${clientId}?year=${year}&month=${month}`
      );

      console.log('📅 [useBillingCalendar] Resposta completa:', response);

      // O api.get retorna diretamente o JSON parseado
      // O controller retorna { success: true, data: {...} }
      if (response && typeof response === 'object') {
        if (response.success === true && response.data) {
          setCalendarData(response.data);
          console.log('✅ [useBillingCalendar] Calendário carregado com sucesso');
        } else if (response.success === false) {
          const errorMsg = response.error || 'Erro ao carregar calendário';
          console.error('❌ [useBillingCalendar] API retornou erro:', errorMsg);
          throw new Error(errorMsg);
        } else {
          // Se não tem success, pode ser que a estrutura seja diferente
          console.warn('⚠️ [useBillingCalendar] Resposta sem campo success, tentando usar diretamente');
          setCalendarData(response as any);
        }
      } else {
        throw new Error('Resposta inválida da API');
      }
    } catch (err: any) {
      console.error('❌ [useBillingCalendar] Erro ao buscar calendário:', err);
      console.error('❌ [useBillingCalendar] Tipo do erro:', typeof err);
      console.error('❌ [useBillingCalendar] Erro completo:', JSON.stringify(err, null, 2));
      console.error('❌ [useBillingCalendar] err.response:', err.response);
      console.error('❌ [useBillingCalendar] err.response?.data:', err.response?.data);
      
      const errorMessage = err.response?.data?.error || 
                          err.response?.data?.message || 
                          err.message || 
                          'Erro desconhecido ao carregar calendário';
      
      setError(errorMessage);
      toast({
        title: 'Erro',
        description: errorMessage,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // Buscar eventos de um dia específico
  const fetchDayEvents = async (day: number) => {
    if (!clientId) {
      console.warn('⚠️ [useBillingCalendar] fetchDayEvents chamado sem clientId');
      return;
    }

    console.log('📅 [useBillingCalendar] fetchDayEvents chamado para dia:', day, 'cliente:', clientId, 'ano:', year, 'mês:', month);
    setLoading(true);
    setError(null);

    try {
      const url = `/billing-rules/day-events/${clientId}?year=${year}&month=${month}&day=${day}`;
      console.log('📅 [useBillingCalendar] URL da requisição:', url);
      
      const response = await api.get<any>(url);

      console.log('📅 [useBillingCalendar] Resposta completa do getDayEvents:', response);

      // O api.get retorna diretamente o JSON parseado
      // O controller retorna { success: true, data: {...} }
      if (response && typeof response === 'object') {
        if (response.success === true && response.data) {
          console.log('✅ [useBillingCalendar] Eventos carregados com sucesso:', response.data.events?.length || 0, 'eventos');
          setDayEvents(response.data);
          setSelectedDay(day);
        } else if (response.success === false) {
          const errorMsg = response.error || 'Erro ao carregar eventos do dia';
          console.error('❌ [useBillingCalendar] API retornou erro:', errorMsg);
          throw new Error(errorMsg);
        } else {
          // Se não tem success, pode ser que a estrutura seja diferente
          console.warn('⚠️ [useBillingCalendar] Resposta sem campo success, tentando usar diretamente');
          setDayEvents(response as any);
          setSelectedDay(day);
        }
      } else {
        throw new Error('Resposta inválida da API');
      }
    } catch (err: any) {
      console.error('❌ [useBillingCalendar] Erro ao buscar eventos do dia:', err);
      console.error('❌ [useBillingCalendar] Tipo do erro:', typeof err);
      console.error('❌ [useBillingCalendar] Erro completo:', JSON.stringify(err, null, 2));
      console.error('❌ [useBillingCalendar] err.response:', err.response);
      console.error('❌ [useBillingCalendar] err.response?.data:', err.response?.data);
      
      const errorMessage = err.response?.data?.error || 
                          err.response?.data?.message || 
                          err.message || 
                          'Erro desconhecido ao carregar eventos do dia';
      
      setError(errorMessage);
      toast({
        title: 'Erro',
        description: errorMessage,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // Bloquear dias
  const blockDays = async (data: BlockDaysRequest): Promise<boolean> => {
    try {
      const response = await api.post<{ success: boolean; message?: string; error?: string }>(
        '/billing-rules/block-days',
        data
      );

      if (response.success) {
        toast({
          title: 'Sucesso',
          description: response.message || 'Dias bloqueados com sucesso'
        });
        await fetchCalendarData();
        if (selectedDay && data.blocked_days.includes(selectedDay)) {
          await fetchDayEvents(selectedDay);
        }
        return true;
      } else {
        throw new Error(response.error || 'Erro ao bloquear dias');
      }
    } catch (err: any) {
      console.error('❌ [useBillingCalendar] Erro ao bloquear dias:', err);
      toast({
        title: 'Erro',
        description: err.response?.data?.error || err.message || 'Não foi possível bloquear os dias',
        variant: 'destructive'
      });
      return false;
    }
  };

  // Desbloquear dias
  const unblockDays = async (blockedDayId: number): Promise<boolean> => {
    try {
      const response = await api.delete<{ success: boolean; error?: string }>(
        `/billing-rules/block-days/${blockedDayId}`
      );

      if (response.success) {
        toast({
          title: 'Sucesso',
          description: 'Bloqueio removido com sucesso'
        });
        await fetchCalendarData();
        if (selectedDay) {
          await fetchDayEvents(selectedDay);
        }
        return true;
      } else {
        throw new Error(response.error || 'Erro ao desbloquear dias');
      }
    } catch (err: any) {
      console.error('❌ [useBillingCalendar] Erro ao desbloquear dias:', err);
      toast({
        title: 'Erro',
        description: err.response?.data?.error || err.message || 'Não foi possível desbloquear os dias',
        variant: 'destructive'
      });
      return false;
    }
  };

  // Reagendar evento
  const rescheduleEvent = async (data: RescheduleEventRequest): Promise<boolean> => {
    try {
      const response = await api.post<{ success: boolean; message?: string; error?: string }>(
        '/billing-rules/reschedule',
        data
      );

      if (response.success) {
        toast({
          title: 'Sucesso',
          description: response.message || 'Evento reagendado com sucesso'
        });
        await fetchCalendarData();
        if (selectedDay) {
          await fetchDayEvents(selectedDay);
        }
        return true;
      } else {
        throw new Error(response.error || 'Erro ao reagendar evento');
      }
    } catch (err: any) {
      console.error('❌ [useBillingCalendar] Erro ao reagendar evento:', err);
      toast({
        title: 'Erro',
        description: err.response?.data?.error || err.message || 'Não foi possível reagendar o evento',
        variant: 'destructive'
      });
      return false;
    }
  };

  // Cancelar reagendamento
  const cancelReschedule = async (rescheduleId: number): Promise<boolean> => {
    try {
      const response = await api.delete<{ success: boolean; error?: string }>(
        `/billing-rules/reschedule/${rescheduleId}`
      );

      if (response.success) {
        toast({
          title: 'Sucesso',
          description: 'Reagendamento cancelado com sucesso'
        });
        await fetchCalendarData();
        if (selectedDay) {
          await fetchDayEvents(selectedDay);
        }
        return true;
      } else {
        throw new Error(response.error || 'Erro ao cancelar reagendamento');
      }
    } catch (err: any) {
      console.error('❌ [useBillingCalendar] Erro ao cancelar reagendamento:', err);
      toast({
        title: 'Erro',
        description: err.response?.data?.error || err.message || 'Não foi possível cancelar o reagendamento',
        variant: 'destructive'
      });
      return false;
    }
  };

  // Navegação do calendário
  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    setSelectedDay(null);
    setDayEvents(null);
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    setSelectedDay(null);
    setDayEvents(null);
  };

  const goToCurrentMonth = () => {
    setCurrentDate(new Date());
    setSelectedDay(null);
    setDayEvents(null);
  };

  const handleDayClick = (day: number) => {
    fetchDayEvents(day);
  };

  // Gerar dados do calendário
  const calendarDays = useMemo((): CalendarDay[] => {
    if (!calendarData) return [];

    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days: CalendarDay[] = [];
    const today = new Date();

    // Gerar 42 dias (6 semanas)
    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);

      const day = date.getDate();
      const isCurrentMonth = date.getMonth() === month - 1;
      const isToday = date.toDateString() === today.toDateString();

      const dayData = calendarData.days.find(d => d.day === day);
      const hasEvents = dayData ? dayData.events.length > 0 : false;
      const eventCount = dayData ? dayData.events.length : 0;
      const isBlocked = dayData?.isBlocked || false;
      const hasRescheduled = dayData?.hasRescheduled || false;

      days.push({
        date,
        day,
        isCurrentMonth,
        isToday,
        hasEvents,
        eventCount,
        clients: hasEvents ? [calendarData.client.name] : []
      });
    }

    return days;
  }, [calendarData, year, month]);

  // Carregar dados quando mudar mês/ano ou clientId
  useEffect(() => {
    if (clientId) {
      fetchCalendarData();
    }
  }, [clientId, year, month]);

  return {
    currentDate,
    calendarData,
    dayEvents,
    selectedDay,
    loading,
    error,
    calendarDays,
    goToPreviousMonth,
    goToNextMonth,
    goToCurrentMonth,
    handleDayClick,
    blockDays,
    unblockDays,
    rescheduleEvent,
    cancelReschedule,
    refreshCalendar: fetchCalendarData,
    refreshDayEvents: () => selectedDay && fetchDayEvents(selectedDay)
  };
};

